import React, { useEffect, useMemo, useRef } from "react";
import {
  View,
  StyleSheet,
  ScrollView,
  Platform,
  Pressable,
  Text,
  useWindowDimensions,
} from "react-native";

import { useTheme } from "react-native-paper";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withSequence,
  withTiming,
  Easing,
} from "react-native-reanimated";

import { useGameStore } from "../stores/useGameStoreFacade";
import { useAssetStore } from "../stores/useAssetStore";
import type { ExtendedTheme } from "../theme/SynapseTheme";
import { chipLabelLayout } from "../utils/chipLabel";
import { withAlpha } from "../utils/color";
import { useShownBestNext } from "../dev/hooks";
import { optionWords, useOptionHover } from "./board/options";
import DevChipRing from "./dev/DevChipRing";

/**
 * On web: wraps children in a View whose DOM node receives a `contextmenu` listener
 * that preventDefault()s (suppressing the browser menu) and calls onWordDefinition.
 * On native: renders children in the same sized cell, with no listener.
 */
const WebRightClick: React.FC<{
  word: string;
  onWordDefinition?: (word: string) => void;
  children: React.ReactNode;
}> = ({ word, onWordDefinition, children }) => {
  const ref = useRef<View>(null);

  useEffect(() => {
    if (Platform.OS !== "web") return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const node = ref.current as any;
    if (!node) return;
    const handler = (e: Event) => {
      e.preventDefault();
      onWordDefinition?.(word);
    };
    node.addEventListener("contextmenu", handler);
    return () => node.removeEventListener("contextmenu", handler);
  }, [word, onWordDefinition]);

  if (Platform.OS !== "web") {
    return <View style={styles.cell}>{children}</View>;
  }
  return (
    <View ref={ref} style={styles.cell}>
      {children}
    </View>
  );
};

/**
 * The tutorial's hinted chip: a current-node border whose opacity breathes
 * 0.6 -> 1 with a soft glow, drawn over the chip so its layout never shifts.
 */
const HintGlow: React.FC<{ color: string }> = ({ color }) => {
  const glow = useSharedValue(0.6);
  useEffect(() => {
    glow.value = withRepeat(
      withSequence(
        withTiming(1, { duration: 1100, easing: Easing.inOut(Easing.ease) }),
        withTiming(0.6, { duration: 1100, easing: Easing.inOut(Easing.ease) }),
      ),
      -1,
      false,
    );
  }, [glow]);
  const glowStyle = useAnimatedStyle(() => ({ opacity: glow.value }));
  return (
    <Animated.View
      pointerEvents="none"
      testID="hint-glow"
      style={[
        styles.hintGlow,
        {
          borderColor: color,
          shadowColor: color,
        },
        glowStyle,
      ]}
    />
  );
};

interface WordChipProps {
  word: string;
  highlighted: boolean;
  /** Its option node on the board is hovered. */
  linked?: boolean;
  windowWidth: number;
  onPress: () => void;
  onLongPress: () => void;
}

const WordChip: React.FC<WordChipProps> = ({
  word,
  highlighted,
  linked = false,
  windowWidth,
  onPress,
  onLongPress,
}) => {
  const { colors, customColors } = useTheme() as ExtendedTheme;
  const setHovered = useOptionHover((s) => s.setHovered);
  const clear = useOptionHover((s) => s.clear);
  useEffect(() => () => clear(word), [word, clear]);
  // Hovering or focusing a button highlights its option node on the board.
  const link = {
    onHoverIn: () => setHovered(word),
    onHoverOut: () => clear(word),
    onFocus: () => setHovered(word),
    onBlur: () => clear(word),
  };
  const hint = customColors.currentNode;
  const devNext = useShownBestNext();
  const label = chipLabelLayout(word, windowWidth, windowWidth < 360 ? 14 : 15);
  return (
    <Pressable
      onPress={onPress}
      onLongPress={onLongPress}
      {...link}
      accessibilityRole="button"
      accessibilityLabel={word}
      testID={`word-chip-${word}`}
      style={(state) => {
        // `hovered` exists on react-native-web only
        const { pressed, hovered } = state as {
          pressed: boolean;
          hovered?: boolean;
        };
        return [
          styles.wordChip,
          { borderColor: highlighted ? hint : colors.onSurfaceVariant },
          highlighted && { backgroundColor: withAlpha(hint, 0.12) },
          !highlighted &&
            (pressed || hovered || linked) && {
              backgroundColor: pressed
                ? "rgba(255,255,255,0.08)"
                : "rgba(255,255,255,0.06)",
              borderColor: colors.onSurface,
            },
        ];
      }}
    >
      {highlighted && <HintGlow color={hint} />}
      {devNext === word && <DevChipRing />}
      <Text
        testID={`word-chip-label-${word}`}
        numberOfLines={label.numberOfLines}
        adjustsFontSizeToFit
        minimumFontScale={0.8}
        style={[
          styles.chipText,
          {
            color: colors.onSurface,
            fontSize: label.fontSize,
            lineHeight: Math.round(label.fontSize * 1.2),
          },
        ]}
      >
        {word}
      </Text>
    </Pressable>
  );
};

// Component to display available neighboring words for selection
const AvailableWordsDisplay = ({
  onWordSelect,
  highlightWord,
  onWordDefinition,
}: {
  onWordSelect: (word: string) => void;
  highlightWord?: string | null;
  onWordDefinition?: (word: string) => void;
}) => {
  const { width } = useWindowDimensions();

  // Get the graph data, current word, and available words from the store
  const graphData = useAssetStore((state) => state.graphData);
  const currentWord = useGameStore((state) => state.currentWord);
  const gameStatus = useGameStore((state) => state.gameStatus);

  // Only allow selection when the game is in playing mode
  const isPlaying = gameStatus === "playing";

  // The same list, in the same order, as the option nodes on the board.
  const availableWords = useMemo(
    () => (isPlaying ? optionWords(graphData, currentWord) : []),
    [graphData, currentWord, isPlaying],
  );
  const boardHovered = useOptionHover((s) => s.hovered);

  if (!isPlaying || !availableWords.length) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.transparentContent}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.content}
        >
          {availableWords.map((word) => (
            <WebRightClick
              key={word}
              word={word}
              onWordDefinition={onWordDefinition}
            >
              <WordChip
                word={word}
                highlighted={highlightWord === word}
                linked={boardHovered === word}
                windowWidth={width}
                onPress={() => onWordSelect(word)}
                onLongPress={() => onWordDefinition?.(word)}
              />
            </WebRightClick>
          ))}
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    margin: 5,
    maxWidth: 600,
    alignSelf: "center",
    width: "100%",
  },
  transparentContent: {
    backgroundColor: "transparent",
  },
  scrollView: {
    maxHeight: 250,
  },
  content: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 4,
    columnGap: 8,
    rowGap: 8,
    paddingHorizontal: 8,
  },
  // Three chips per row on every width (two rows for six options).
  cell: {
    width: "31%",
  },
  wordChip: {
    width: "100%",
    height: 44,
    borderRadius: 10,
    borderWidth: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 6,
    backgroundColor: "transparent",
  },
  chipText: {
    fontWeight: "500",
    textAlign: "center",
    width: "100%",
  },
  hintGlow: {
    position: "absolute",
    top: -1,
    left: -1,
    right: -1,
    bottom: -1,
    borderRadius: 10,
    borderWidth: 2,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.6,
    shadowRadius: 8,
  },
});

export default AvailableWordsDisplay;
